import {
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

import {
  createCompanyManage,
  deactivateCompany,
  getCompanyQuestionTemplate,
  getCompanyManageDetail,
  getCompanyManageList,
  reactivateCompany,
  upsertCompanyQuestionTemplate,
  updateCompanyDefaultsManage,
  updateCompanyManage,
} from "@/app/api/company-manage";
import { userService } from "@/app/api/user";
import { useUserContext } from "@/context/user.context";
import { useAuthedQuery } from "@/hooks/queries/use-authed-query.hook";
import type {
  CompanyQuestionTemplate,
  CompanyManageDetail,
  CompanyManageListItem,
  UpdateCompanyDefaultsManageMutationPayload,
  UpdateCompanyManageMutationPayload,
  UpsertCompanyQuestionTemplateMutationPayload,
} from "@/types/company-manage";
import type { DefaultUseQueryConfigParam } from "@/types/third-party";
import type { Nullable } from "@/types/utils";
import { clearLocalStorageTableState } from "@/utils/local-storage";

const COMPANY_QUERY_KEY_BASE = "company";
const COMPANY_QUERY_KEYS = {
  base: [COMPANY_QUERY_KEY_BASE] as const,
  manage: [COMPANY_QUERY_KEY_BASE, "manage"] as const,
  manageDetail: (companyId: string) =>
    [COMPANY_QUERY_KEY_BASE, "manage", "detail", companyId] as const,
  questionTemplate: (companyId: string) =>
    [COMPANY_QUERY_KEY_BASE, "manage", "question-template", companyId] as const,
};

interface CompanyListItem {
  id: string;
  name: string;
}

export const useCompaniesListQuery = (
  config: Partial<UseQueryOptions<CompanyListItem[]>> = {},
) => {
  return useAuthedQuery({
    queryFn: () =>
      userService.axiosWithUserAuth
        .get<CompanyListItem[]>("company/cx/companies")
        .then(({ data }) => data),
    queryKey: COMPANY_QUERY_KEYS.base,
    ...config,
  });
};

export const useCompanyImpersonationMutation = () => {
  const { user } = useUserContext();

  return useMutation({
    mutationFn: (companyId: string) =>
      userService.axiosWithUserAuth.patch(`user/impersonate/${companyId}`),
    onSuccess: () => {
      if (user) {
        clearLocalStorageTableState(user.id);
      }

      if (window.location.pathname === "/") {
        window.location.reload();
      } else {
        window.location.assign("/");
      }
    },
  });
};

// Company Management (internal-only)

export const useCompanyManageListQuery = (
  config: DefaultUseQueryConfigParam<CompanyManageListItem[]> = {},
) => {
  return useAuthedQuery({
    queryFn: getCompanyManageList,
    queryKey: COMPANY_QUERY_KEYS.manage,
    ...config,
  });
};

export const useCompanyManageDetailQuery = (
  companyId: Nullable<string>,
  config: DefaultUseQueryConfigParam<CompanyManageDetail> = {},
) => {
  const enabled = Boolean(companyId) && (config.enabled ?? true);

  return useAuthedQuery({
    queryFn: () => {
      if (!companyId) {
        throw new Error("companyId is required");
      }

      return getCompanyManageDetail(companyId);
    },
    queryKey: COMPANY_QUERY_KEYS.manageDetail(companyId ?? ""),
    ...config,
    enabled,
  });
};

export const useCompanyQuestionTemplateQuery = (
  companyId: Nullable<string>,
  config: DefaultUseQueryConfigParam<CompanyQuestionTemplate | null> = {},
) => {
  const enabled = Boolean(companyId) && (config.enabled ?? true);

  return useAuthedQuery({
    queryFn: () => {
      if (!companyId) {
        throw new Error("companyId is required");
      }

      return getCompanyQuestionTemplate(companyId);
    },
    queryKey: COMPANY_QUERY_KEYS.questionTemplate(companyId ?? ""),
    ...config,
    enabled,
  });
};

export const useCreateCompanyManageMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => createCompanyManage(name),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.base });
    },
  });
};

export const useUpdateCompanyManageMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      ...payload
    }: UpdateCompanyManageMutationPayload) =>
      updateCompanyManage(companyId, payload),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.base });
    },
  });
};

export const useUpsertCompanyQuestionTemplateMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      ...payload
    }: UpsertCompanyQuestionTemplateMutationPayload) =>
      upsertCompanyQuestionTemplate(companyId, payload),
    onSuccess: (_template, variables) => {
      client.invalidateQueries({
        queryKey: COMPANY_QUERY_KEYS.questionTemplate(variables.companyId),
      });
    },
  });
};

export const useUpdateCompanyDefaultsManageMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      ...payload
    }: UpdateCompanyDefaultsManageMutationPayload) =>
      updateCompanyDefaultsManage(companyId, payload),
    onSuccess: (_defaults, variables) => {
      client.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.base });
      client.invalidateQueries({
        queryKey: COMPANY_QUERY_KEYS.manageDetail(variables.companyId),
      });
      client.invalidateQueries({
        queryKey: COMPANY_QUERY_KEYS.questionTemplate(variables.companyId),
      });
    },
  });
};

export const useDeactivateCompanyMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (companyId: string) => deactivateCompany(companyId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.base });
    },
  });
};

export const useReactivateCompanyMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (companyId: string) => reactivateCompany(companyId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.base });
    },
  });
};
